import { getDirectParentFilter } from '@databases/filters/utils/getDirectParentFilter';
import { getNestedFilter } from '@databases/filters/utils/getNestedFilter';
// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode } from '@databases/types';
import {
  type Database,
  type DatabaseField,
  DatabaseFieldType,
  type DatabaseValue,
  type EntryWithContext,
  type ValueWithContext,
} from '@databases/types/database';
import {
  type DatabaseCalculationType,
  type DatabaseCardWidth,
  type DatabaseCoverFieldType,
  type DatabaseFilter,
  type DatabaseFilterBooleanExpression,
  DatabaseFilterType,
  type DatabaseFilterUsageType,
  DatabaseGroupSortType,
  type DatabaseLayout,
  type DatabaseLayoutType,
  type DatabaseView,
  type DatabaseViewSettings,
  DEFAULT_DATABASE_VIEW_SETTINGS,
  DEFAULT_FIELD_VISIBILITY_ID,
  isFilterWithUsageType,
  shouldShowField,
  ViewId,
} from '@databases/types/database/view';
import { DatabaseChangeType } from '@databases/types/redux';
import { clone, cloneDeep, omit } from 'lodash-es';
import { type Op } from 'quill-delta';
import { v4 as uuidv4 } from 'uuid';

import { getExperimentValue } from '@canvas/client-feature-flags';

import { generateId, isEqualOmitUndefined } from '../../utils';
import { type DatabaseChange, type ValueChange } from '../changesets';
import {
  getNumberRepresentation,
  getStringRepresentation,
  isEmptyValue,
  isEqualValue,
} from '../field-types';
import { toDisplayDate } from '../field-types/date';
import {
  generateAllValues,
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseEntryRequired,
  getDatabaseFieldOptional,
  getDatabaseFields,
} from '../utils';

import { isSortWithField, sortEntriesMulti } from './sort';

/**
 * Returns true if the given database entry is matched by the given database filter.
 *
 * If the database filter references a field that does not exist (anymore), or the filter has an unknown type,
 * true is returned. This is to ensure that when a filter is not fully configured yet (for example no field
 * has been selected yet), or when a field that is referenced by a filter has been removed, the filter does not
 * cause the database to be empty.
 *
 * If the database filter is a filter group (denoted by its boolean typing) and it has no children filters,
 * false is returned if its direct parent is an AND boolean group and true is returned if its direct parent is
 * an OR boolean group. This is to ensure that when a filter group is not fully configured yet and has no
 * children filters, the filter group does not cause the database to be empty or display all entries.
 */
export function matchesFilter(
  { database, entry }: EntryWithContext,
  filter: DatabaseFilter,
  state: DefaultRootState | undefined,
  parentFilter?: DatabaseFilterBooleanExpression
): boolean {
  const isContextualMenusEnabled = getExperimentValue(
    'databasesViewContextualMenus',
    'enabled',
    {
      fireExperimentExposure: false,
    }
  );

  switch (filter.type) {
    case DatabaseFilterType.AND: {
      if (parentFilter && filter.value.length === 0) {
        if (parentFilter.type === DatabaseFilterType.AND) {
          return true;
        } else if (parentFilter.type === DatabaseFilterType.OR) {
          return false;
        }
      }

      return filter.value.every((f) =>
        matchesFilter({ database, entry }, f, state, filter)
      );
    }

    case DatabaseFilterType.OR: {
      if (parentFilter && filter.value.length === 0) {
        if (parentFilter.type === DatabaseFilterType.AND) {
          return true;
        } else if (parentFilter.type === DatabaseFilterType.OR) {
          return false;
        }
      }

      return filter.value.some((f) =>
        matchesFilter({ database, entry }, f, state, filter)
      );
    }

    case DatabaseFilterType.EQUALS: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      // Skip filtering when the filter value is empty (e.g. user just added a filter but hasn't set a value yet)
      if (
        isContextualMenusEnabled &&
        isEmptyValue({ database, field, value: filter.value }, state)
      ) {
        return true;
      }
      const value = getConvertedDatabaseValue(database, field, entry, state);
      const result = isEqualValue(
        { database, field, value: filter.value },
        { database, field, entry, value },
        state
      );
      return filter.not ? !result : result;
    }

    case DatabaseFilterType.CONTAINS: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      // Skip filtering when the filter value is empty (e.g. user just added a filter but hasn't set a value yet)
      if (isContextualMenusEnabled && !filter.value) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const result = value
        ? (getStringRepresentation({ database, field, entry, value }, state) || '')
            .toLowerCase()
            .includes(filter.value.toLowerCase())
        : true;
      return filter.not ? !result : result;
    }

    case DatabaseFilterType.EMPTY: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const result = value
        ? isEmptyValue({ database, field, entry, value }, state)
        : true;
      return filter.not ? !result : result;
    }

    case DatabaseFilterType.GREATER: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      // Skip filtering when the filter value is empty (e.g. user just added a filter but hasn't set a value yet)
      if (
        isContextualMenusEnabled &&
        isEmptyValue({ database, field, value: filter.value }, state)
      ) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const valueNumber = getNumberRepresentation(
        { database, field, entry, value },
        state
      );
      const filterNumber = getNumberRepresentation(
        { database, field, entry, value: filter.value },
        state
      );
      return (
        valueNumber != null &&
        filterNumber != null &&
        (filter.orEqual ? valueNumber >= filterNumber : valueNumber > filterNumber)
      );
    }

    case DatabaseFilterType.LESS: {
      const field = getDatabaseFieldOptional(database, filter.fieldId);
      if (!field) {
        return true;
      }
      // Skip filtering when the filter value is empty (e.g. user just added a filter but hasn't set a value yet)
      if (
        isContextualMenusEnabled &&
        isEmptyValue({ database, field, value: filter.value }, state)
      ) {
        return true;
      }
      const value = field && getConvertedDatabaseValue(database, field, entry, state);
      const valueNumber = getNumberRepresentation(
        { database, field, entry, value },
        state
      );
      const filterNumber = getNumberRepresentation(
        { database, field, entry, value: filter.value },
        state
      );
      return (
        valueNumber != null &&
        filterNumber != null &&
        (filter.orEqual ? valueNumber <= filterNumber : valueNumber < filterNumber)
      );
    }

    default:
      return true;
  }
}

/**
 * Returns the "number of active filters" as it should be presented for the user for the specified database filter.
 */
export function getFilterCount(filter: DatabaseFilter | undefined): number {
  if (filter == null) {
    return 0;
  } else if (
    filter.type === DatabaseFilterType.AND ||
    filter.type === DatabaseFilterType.OR
  ) {
    return filter.value.reduce((c, f) => c + getFilterCount(f), 0);
  } else {
    return 1;
  }
}

/**
 * Returns a copy of the database draft, with the filters and sort options of the given database view applied. filter, sort and fields
 * settings are applied to the saved version of the database to make sure that nothing jumps around while editing values, but the resulting
 * view contains the values of the database draft.
 * @param savedDatabase The current saved version of the database.
 * @param databaseDraft The current draft of the database. May be set to the same as savedDatabase if no draft exists.
 */
export function applyDatabaseView<Db extends Database>(
  savedDatabase: Database,
  databaseDraft: Db,
  viewSettings: DatabaseViewSettings = DEFAULT_DATABASE_VIEW_SETTINGS,
  state: DefaultRootState | undefined,
  ignoredEntries: string[] = []
): Db {
  let result = { ...databaseDraft };

  if (viewSettings.filter) {
    result = {
      ...result,
      entries: result.entries.filter((entryId) => {
        const savedEntry = getDatabaseEntryOptional(databaseDraft, entryId);
        if (!savedEntry) {
          return true;
        } else {
          return matchesFilter(
            { database: databaseDraft, entry: savedEntry },
            viewSettings.filter!,
            state
          );
        }
      }),
    };
  }

  if (viewSettings.sorts) {
    const sortsWithField = viewSettings.sorts
      .map((sort) => ({
        ...sort,
        field: getDatabaseFieldOptional(databaseDraft, sort.fieldId),
      }))
      .filter(isSortWithField);

    if (sortsWithField.length > 0) {
      result = {
        ...result,
        entries: sortEntriesMulti(
          databaseDraft,
          [...result.entries],
          sortsWithField,
          state,
          ignoredEntries
        ),
      };
    }
  }

  if (viewSettings.fields) {
    result = {
      ...result,
      fields: result.fields.filter(
        (fieldId) =>
          !savedDatabase.fields.includes(fieldId) || // Always show newly added fields, regardless of field selection
          shouldShowField(viewSettings.fields, fieldId)
      ),
    };
  }

  if (viewSettings.search) {
    result = {
      ...result,
      entries: result.entries.filter((entryId) => {
        const entry = getDatabaseEntryRequired(result, entryId);
        return getDatabaseFields(result).some((field) => {
          const value = getConvertedDatabaseValue(result, field, entry, state);

          const displayFormattedDate =
            value.type === DatabaseFieldType.DATE &&
            value.value &&
            toDisplayDate(value.value);

          const stringRepresentation = displayFormattedDate
            ? displayFormattedDate
            : getStringRepresentation({ database: result, field, entry, value }, state);

          return value
            ? (stringRepresentation || '')
                .toLowerCase()
                .includes(viewSettings.search!.toLowerCase())
            : true;
        });
      }),
    };
  }

  return result;
}

/**
 * Returns database values by field ID that attempt to match the given filter. This is only possible for
 * some filter types (in particular an equals filter). The purpose is to set these values when creating
 * a new database entry so that the new entry matches the current filter.
 */
export function getDefaultValuesForFilter(
  filter: DatabaseFilter | undefined
): Record<string, DatabaseValue> {
  const result: Record<string, DatabaseValue> = {};

  switch (filter?.type) {
    case DatabaseFilterType.AND:
    case DatabaseFilterType.OR:
      for (const f of filter.value) {
        Object.assign(result, getDefaultValuesForFilter(f));
      }
      break;

    case DatabaseFilterType.EQUALS:
      if (!filter.not) {
        result[filter.fieldId] = clone(filter.value);
      }
      break;
  }

  return result;
}

/**
 * Get only database values of the database which has the same fieldId as the predefined filters.
 * Handles filter groups recursively (though currently we don't support filter groups with a depth greater than 1)
 */
export const getFilteredValuesWithContext = (
  database: Database,
  filter: DatabaseFilter
): ValueWithContext[] => {
  const filterFieldIds: string[] = [];
  const getFilterFieldIds = (filter: DatabaseFilter) => {
    if (isFilterWithUsageType(filter)) {
      for (const filterValue of filter.value) {
        getFilterFieldIds(filterValue);
      }
    } else {
      filterFieldIds.push(filter.fieldId);
    }
  };

  getFilterFieldIds(filter);

  return [...generateAllValues(database)].filter((context) => {
    return filterFieldIds.includes(context.field.id);
  });
};

export function isValidDateField(groupField: DatabaseField): boolean {
  return groupField.type === DatabaseFieldType.DATE;
}

/**
 * Returns the date field configured in the given layout settings. If no valid date field is configured, undefined is returned.
 */
export function getDateField(
  database: Database<ObjectMode>,
  layout: DatabaseLayout
): DatabaseField | undefined {
  const dateField = getDatabaseFieldOptional(database, layout.dateFieldId);
  return dateField && isValidDateField(dateField) ? dateField : undefined;
}

export function getViewById(
  views: DatabaseView[],
  viewId: string | null
): DatabaseView | undefined {
  return views.find((value) => value.id === viewId);
}

export function getDefaultView(database: Database): DatabaseView {
  const foundView =
    database.views.find((view) => view.id === database.defaultViewId) ||
    database.views[0];

  // If no views exist, return a hardcoded default view. This is only relevant for AIFC.
  // We prevent the initial creation of the database from crashing, since AIFC will immediately create one.
  if (!foundView) {
    return {
      id: ViewId.DEFAULT,
      name: 'Default View',
      settings: DEFAULT_DATABASE_VIEW_SETTINGS,
    };
  }

  return foundView;
}

export function viewHasChanges(
  viewSettings: DatabaseViewSettings | undefined,
  other: DatabaseViewSettings | undefined
): boolean {
  return !isEqualOmitUndefined(omit(viewSettings, ['search']), omit(other, ['search']));
}

export function createView(
  viewSettings: DatabaseViewSettings = DEFAULT_DATABASE_VIEW_SETTINGS
): DatabaseView {
  const id = generateId();
  return {
    id,
    settings: viewSettings,
    name: '',
  };
}

export function isUnsavedView(view: DatabaseView): boolean {
  return view?.id === ViewId.UNSAVED;
}

export enum ViewFilterChangeType {
  VIEW_FILTERS_SET_FILTER_USAGE_TYPE = 'view_filters_set_filter_usage_type',
  VIEW_FILTERS_SET_FIELD_ID = 'view_filters_set_field_id',
  VIEW_FILTERS_SET_FILTER_TYPE = 'view_filters_set_filter_type',
  VIEW_FILTERS_SET_VALUE = 'view_filters_set_value',
}

export enum ViewChangeTypes {
  LAYOUT_TYPE = 'layout_type',
  LAYOUT_CARD_WIDTH = 'layout_card_width',
  LAYOUT_COVER_FIELD_ID = 'layout_cover_field_id',
  LAYOUT_SHOW_FIELD_NAMES = 'layout_show_field_names',
  LAYOUT_GROUP_FIELD_ID = 'layout_group_field_id',
  LAYOUT_DATE_FIELD_ID = 'layout_date_field_id',
  LAYOUT_SHOW_NUMBERED_ENTRIES = 'layout_show_numbered_entries',
  VIEW_NAME = 'view_name',
  VIEW_FIELDS = 'view_fields',
  VIEW_SORT = 'view_sort',
  VIEW_SORT_UPDATE_VALUE = 'view_sort_update_value',
  VIEW_SORT_INSERT = 'view_sort_insert',
  VIEW_SORT_APPEND = 'view_sort_append',
  VIEW_SORT_DELETE = 'view_sort_delete',
  VIEW_SORT_REORDER = 'view_sort_reorder',
  VIEW_SORT_CLEAR = 'view_sort_clear',
  VIEW_FILTERS_SET = 'view_filters_set',
  VIEW_FILTERS_APPEND = 'view_filters_append',
  VIEW_FILTERS_REMOVE = 'view_filters_remove',
  VIEW_FILTERS_CLEAR = 'view_filters_clear',
  VIEW_SEARCH = 'view_search',
  VIEW_CALCULATIONS_SET = 'view_calculations_set',
  VIEW_CALCULATIONS_REMOVE = 'view_calculations_remove',
  VIEW_GROUPING_FIELD = 'view_grouping_field',
  VIEW_GROUPING_SORT_TYPE = 'view_grouping_sort_type',
  VIEW_GROUPING_MANUAL_GROUP_ORDER = 'view_grouping_manual_group_order',
  VIEW_GROUPING_CLEAR = 'view_grouping_clear',
}

export type ViewChangeType =
  | {
      type: ViewChangeTypes.LAYOUT_TYPE;
      value: DatabaseLayoutType;
    }
  | {
      type: ViewChangeTypes.LAYOUT_CARD_WIDTH;
      value: DatabaseCardWidth;
    }
  | {
      type: ViewChangeTypes.LAYOUT_COVER_FIELD_ID;
      value: DatabaseCoverFieldType | string;
    }
  | {
      type: ViewChangeTypes.LAYOUT_SHOW_FIELD_NAMES;
      value: boolean;
    }
  | {
      type: ViewChangeTypes.LAYOUT_GROUP_FIELD_ID;
      value?: string;
    }
  | {
      type: ViewChangeTypes.VIEW_GROUPING_FIELD;
      fieldId: string;
      isSubgroup?: boolean;
      manualGroupOrder?: string[];
    }
  | {
      type: ViewChangeTypes.VIEW_GROUPING_SORT_TYPE;
      sortType: DatabaseGroupSortType;
      isSubgroup?: boolean;
    }
  | {
      type: ViewChangeTypes.VIEW_GROUPING_MANUAL_GROUP_ORDER;
      manualGroupOrder: string[];
      isSubgroup?: boolean;
    }
  | {
      type: ViewChangeTypes.VIEW_GROUPING_CLEAR;
      isSubgroup?: boolean;
    }
  | {
      type: ViewChangeTypes.LAYOUT_DATE_FIELD_ID;
      value?: string;
    }
  | {
      type: ViewChangeTypes.LAYOUT_SHOW_NUMBERED_ENTRIES;
      value: boolean;
    }
  | {
      type: ViewChangeTypes.VIEW_NAME;
      value: string;
      delta: Op[];
    }
  | {
      type: ViewChangeTypes.VIEW_FIELDS;
      fieldKey: string;
      fieldValue: boolean;
      fields?: DatabaseField[];
    }
  | {
      type: ViewChangeTypes.VIEW_SORT;
      sort:
        | {
            fieldId: string;
            ascending: boolean;
          }
        | undefined;
    }
  | {
      type: ViewChangeTypes.VIEW_SORT_UPDATE_VALUE;
      fieldId: string;
      ascending: boolean;
      customOrder?: boolean;
    }
  | {
      type: ViewChangeTypes.VIEW_SORT_INSERT;
      sort: {
        fieldId: string;
        ascending: boolean;
        customOrder?: boolean;
      };
      index: number;
    }
  | {
      type: ViewChangeTypes.VIEW_SORT_APPEND;
      sort: {
        fieldId: string;
        ascending: boolean;
        customOrder?: boolean;
      };
    }
  | {
      type: ViewChangeTypes.VIEW_SORT_DELETE;
      fieldId: string;
    }
  | {
      type: ViewChangeTypes.VIEW_SORT_REORDER;
      fieldId: string;
      newIndex: number;
    }
  | {
      type: ViewChangeTypes.VIEW_SORT_CLEAR;
    }
  | {
      type:
        | ViewChangeTypes.VIEW_FILTERS_SET
        | ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID
        | ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE;
      filter: DatabaseFilter;
      path?: number[];
    }
  | {
      type: ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE;
      usageType: DatabaseFilterUsageType;
      usageFilterId?: string;
      path?: number[];
    }
  | {
      type: ViewFilterChangeType.VIEW_FILTERS_SET_VALUE;
      filter: DatabaseFilter;
      path?: number[];
      change: ValueChange | null;
    }
  | {
      type: ViewChangeTypes.VIEW_FILTERS_APPEND;
      filterGroupId?: string;
      filterGroupPath?: number[];
      filter: DatabaseFilter;
      path?: number[];
    }
  | {
      type: ViewChangeTypes.VIEW_FILTERS_REMOVE;
      filterId: string;
      path?: number[];
    }
  | {
      type: ViewChangeTypes.VIEW_FILTERS_CLEAR;
    }
  | {
      type: ViewChangeTypes.VIEW_SEARCH;
      value?: string;
    }
  | {
      type: ViewChangeTypes.VIEW_CALCULATIONS_SET;
      fieldKey: string;
      value: DatabaseCalculationType;
    }
  | {
      type: ViewChangeTypes.VIEW_CALCULATIONS_REMOVE;
      fieldKey: string;
    };

export function applyViewChange(view: DatabaseView, data: ViewChangeType): DatabaseView {
  if (data.type === ViewChangeTypes.VIEW_NAME) {
    return {
      ...view,
      name: data.value,
    };
  } else {
    return {
      ...view,
      settings: applyViewSettingsChange(view.settings, data),
    };
  }
}

function getFieldGrouping(
  viewSettings: DatabaseViewSettings,
  isSubgroup: boolean | undefined
) {
  const fieldGroupingType = isSubgroup ? 'subgroupBy' : 'groupBy';
  const fieldGrouping =
    viewSettings.grouping?.[fieldGroupingType as keyof typeof viewSettings.grouping];
  return { fieldGroupingType, fieldGrouping };
}

export function applyViewSettingsChange(
  viewSettings: DatabaseViewSettings,
  data: ViewChangeType
): DatabaseViewSettings {
  switch (data.type) {
    case ViewChangeTypes.LAYOUT_TYPE: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          type: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_CARD_WIDTH: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          cardWidth: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_COVER_FIELD_ID: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          coverFieldId: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_SHOW_FIELD_NAMES: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          showFieldNames: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_GROUP_FIELD_ID: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          groupFieldId: data.value,
        },
      };
    }
    case ViewChangeTypes.VIEW_GROUPING_FIELD: {
      const { fieldGroupingType, fieldGrouping } = getFieldGrouping(
        viewSettings,
        data.isSubgroup
      );
      const sortType = data.manualGroupOrder
        ? DatabaseGroupSortType.MANUAL
        : DatabaseGroupSortType.ASCENDING;
      return {
        ...viewSettings,
        grouping: {
          ...viewSettings.grouping,
          [fieldGroupingType]: {
            ...fieldGrouping,
            fieldId: data.fieldId,
            sortType,
            manualGroupOrder: data?.manualGroupOrder || [],
          },
        },
      };
    }
    case ViewChangeTypes.VIEW_GROUPING_SORT_TYPE: {
      const { fieldGroupingType, fieldGrouping } = getFieldGrouping(
        viewSettings,
        data.isSubgroup
      );

      if (fieldGrouping) {
        return {
          ...viewSettings,
          grouping: {
            ...viewSettings.grouping,
            [fieldGroupingType]: {
              ...fieldGrouping,
              sortType: data.sortType,
            },
          },
        };
      }

      return viewSettings;
    }

    case ViewChangeTypes.VIEW_GROUPING_MANUAL_GROUP_ORDER: {
      const { fieldGroupingType, fieldGrouping } = getFieldGrouping(
        viewSettings,
        data.isSubgroup
      );

      if (fieldGrouping) {
        return {
          ...viewSettings,
          grouping: {
            ...viewSettings.grouping,
            [fieldGroupingType]: {
              ...fieldGrouping,
              manualGroupOrder: data.manualGroupOrder,
            },
          },
        };
      }

      return viewSettings;
    }
    case ViewChangeTypes.VIEW_GROUPING_CLEAR: {
      const { fieldGroupingType, fieldGrouping } = getFieldGrouping(
        viewSettings,
        data.isSubgroup
      );
      if (fieldGrouping) {
        return {
          ...viewSettings,
          grouping: {
            ...viewSettings.grouping,
            [fieldGroupingType]: undefined,
          },
        };
      }
      return viewSettings;
    }
    case ViewChangeTypes.LAYOUT_DATE_FIELD_ID: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          dateFieldId: data.value,
        },
      };
    }
    case ViewChangeTypes.LAYOUT_SHOW_NUMBERED_ENTRIES: {
      return {
        ...viewSettings,
        layout: {
          ...viewSettings.layout,
          showNumberedEntries: data.value,
        },
      };
    }
    case ViewChangeTypes.VIEW_FIELDS: {
      let newFields: Record<string, boolean> = {};
      if (viewSettings.fields) {
        newFields = viewSettings.fields;
      } else {
        if (data.fields) {
          newFields = data.fields.reduce((obj, field) => {
            obj[field.id] = true;
            return obj;
          }, {} as Record<string, boolean>);
        }
      }

      newFields = {
        ...newFields,
        [data.fieldKey]: data.fieldValue,
        [DEFAULT_FIELD_VISIBILITY_ID]: true, // for the current view, we want newly added fields to be visible
      };

      return {
        ...viewSettings,
        fields:
          data.fields &&
          data.fields.every((field) => field.id in newFields) &&
          Object.keys(newFields).every(
            (fieldId) =>
              fieldId === DEFAULT_FIELD_VISIBILITY_ID || newFields[fieldId] === true
          )
            ? undefined
            : newFields,
      };
    }
    case ViewChangeTypes.VIEW_SORT: {
      if (data.sort && viewSettings.sorts.length <= 1) {
        return {
          ...viewSettings,
          sort: data.sort,
          sorts: [data.sort],
        };
      }

      if (!data.sort && viewSettings.sorts.length > 0) {
        return {
          ...viewSettings,
          sort: data.sort,
          sorts: [],
        };
      }

      return {
        ...viewSettings,
        sort: data.sort,
      };
    }
    case ViewChangeTypes.VIEW_SORT_UPDATE_VALUE: {
      const sortToUpdateIndex = viewSettings.sorts.findIndex(
        (sort) => sort.fieldId === data.fieldId
      );
      const newSorts = [...viewSettings.sorts];
      if (sortToUpdateIndex !== -1) {
        newSorts[sortToUpdateIndex] = {
          ...newSorts[sortToUpdateIndex],
          ascending: data.ascending,
          customOrder: data.customOrder,
        };
      }
      return {
        ...viewSettings,
        sorts: newSorts,
      };
    }
    case ViewChangeTypes.VIEW_SORT_INSERT: {
      return {
        ...viewSettings,
        sorts: [
          ...viewSettings.sorts.slice(0, data.index),
          data.sort,
          ...viewSettings.sorts.slice(data.index),
        ],
      };
    }
    case ViewChangeTypes.VIEW_SORT_APPEND: {
      return {
        ...viewSettings,
        sorts: viewSettings.sorts.concat(data.sort),
      };
    }
    case ViewChangeTypes.VIEW_SORT_DELETE: {
      const newSorts = viewSettings.sorts.filter((sort) => sort.fieldId !== data.fieldId);
      return {
        ...viewSettings,
        sorts: newSorts,
      };
    }
    case ViewChangeTypes.VIEW_SORT_REORDER: {
      const newSorts = [...viewSettings.sorts];
      const targetSort = newSorts.find((sort) => sort.fieldId === data.fieldId);
      if (targetSort) {
        newSorts.splice(newSorts.indexOf(targetSort), 1);
        newSorts.splice(data.newIndex, 0, targetSort);
      }
      return {
        ...viewSettings,
        sorts: newSorts,
      };
    }
    case ViewChangeTypes.VIEW_SORT_CLEAR: {
      return {
        ...viewSettings,
        sorts: [],
      };
    }
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE: {
      const parentFilter = cloneDeep(viewSettings.filter);

      if (parentFilter && isFilterWithUsageType(parentFilter)) {
        if (data.usageFilterId && data.path) {
          const nestedFilterData = getNestedFilter(
            parentFilter.value,
            data.usageFilterId,
            data.path
          );

          // A filter will be defined if there is a valid filter with an id matching the acted
          // upon filter. This validates that a collaborator has not removed the filter whose
          // value is being set.
          if (nestedFilterData) {
            const [filter, index] = nestedFilterData as [DatabaseFilter, number];

            if (isFilterWithUsageType(filter)) {
              // Operations are done on the direct parent of the altered or new filter
              const directParent = getDirectParentFilter(parentFilter, filter, index) as
                | DatabaseFilterBooleanExpression
                | undefined;

              if (directParent && isFilterWithUsageType(directParent)) {
                directParent.value = [
                  ...directParent.value.slice(0, index),
                  {
                    id: filter.id,
                    type: data.usageType,
                    value: cloneDeep(filter.value),
                  } as DatabaseFilter,
                  ...directParent.value.slice(index + 1),
                ];
              }

              return {
                ...viewSettings,
                filter: parentFilter,
              };
            }
          }
        }

        return {
          ...viewSettings,
          filter: {
            id: parentFilter.id,
            type: data.usageType,
            value: cloneDeep(parentFilter.value),
          },
        };
      } else {
        return {
          ...viewSettings,
          filter: undefined,
        };
      }
    }
    case ViewFilterChangeType.VIEW_FILTERS_SET_VALUE:
      if (!viewSettings.filter || data.path === undefined) {
        return {
          ...viewSettings,
          filter: undefined,
        };
      } else {
        const parentFilter = cloneDeep(viewSettings.filter);

        // If a view has filters, the top level filter for the view will always be a boolean
        // usage type
        if (isFilterWithUsageType(parentFilter)) {
          const nestedFilterData = getNestedFilter(
            parentFilter.value,
            data.filter.id,
            data.path
          );

          // A filter will be defined if there is a valid filter with an id matching the acted
          // upon filter. This validates that a collaborator has not removed the filter whose
          // value is being set.
          if (nestedFilterData) {
            const [filter, index] = nestedFilterData as [DatabaseFilter, number];
            // Operations are done on the direct parent of the altered or new filter
            const directParent = getDirectParentFilter(parentFilter, filter, index) as
              | DatabaseFilter
              | undefined;

            if (
              directParent &&
              isFilterWithUsageType(directParent) &&
              data.filter.type !== DatabaseFilterType.EMPTY
            ) {
              directParent.value = [
                ...directParent.value.slice(0, index),
                {
                  ...directParent.value[index],
                  value: data.filter.value,
                } as DatabaseFilter,
                ...directParent.value.slice(index + 1),
              ];
            }
          }
        }

        return {
          ...viewSettings,
          filter: parentFilter,
        };
      }

    case ViewChangeTypes.VIEW_FILTERS_SET:
    case ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID:
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE:
      if (!data.filter) {
        return {
          ...viewSettings,
          filter: undefined,
        };
      }

      if (viewSettings.filter && data.path) {
        const parentFilter = cloneDeep(viewSettings.filter);

        // If a view has filters, the top level filter for the view will always be a boolean
        // usage type
        if (isFilterWithUsageType(parentFilter)) {
          const nestedFilterData = getNestedFilter(
            parentFilter.value,
            data.filter.id,
            data.path
          );

          // A filter will be defined if there is a valid filter with an id matching the acted
          // upon filter. This validates that a collaborator has not removed the filter whose
          // value is being set.
          if (nestedFilterData) {
            const [filter, index] = nestedFilterData as [DatabaseFilter, number];
            // Operations are done on the direct parent of the altered or new filter
            const directParent = getDirectParentFilter(parentFilter, filter, index) as
              | DatabaseFilter
              | undefined;

            if (directParent && isFilterWithUsageType(directParent)) {
              directParent.value = [
                ...directParent.value.slice(0, index),
                cloneDeep(data.filter),
                ...directParent.value.slice(index + 1),
              ];
            }
          }
        }

        return {
          ...viewSettings,
          filter: parentFilter,
        };
      } else {
        return {
          ...viewSettings,
        };
      }

    case ViewChangeTypes.VIEW_FILTERS_APPEND: {
      if (!viewSettings.filter) {
        return {
          ...viewSettings,
          filter: {
            id: uuidv4(),
            type: DatabaseFilterType.AND,
            value: [cloneDeep(data.filter)],
          },
        };
      } else {
        const parentFilter = cloneDeep(viewSettings.filter);

        // If a view has filters, the top level filter for the view will always be a boolean
        // usage type
        if (isFilterWithUsageType(parentFilter)) {
          if (data.filterGroupId && data.filterGroupPath) {
            // When a new filter is added to a filter group, the parent boolean usage filter will be
            // in the filter array and must be retrieved to add the new filter to
            const nestedFilterData = getNestedFilter(
              parentFilter.value,
              data.filterGroupId,
              data.filterGroupPath
            );

            if (nestedFilterData) {
              const [directParent, index] = nestedFilterData as [
                DatabaseFilterBooleanExpression,
                number
              ];

              // Operations are done on the direct parent of the new filter
              directParent.value = [...directParent.value, cloneDeep(data.filter)];

              // As the filter object of a view is updated on appendage, the appendage
              // made to a filter group must be updated in the view filter object as
              // well
              parentFilter.value = [
                ...parentFilter.value.slice(0, index),
                cloneDeep(directParent),
                ...parentFilter.value.slice(index + 1),
              ];
            }
          } else {
            parentFilter.value = [...parentFilter.value, cloneDeep(data.filter)];
          }
        }

        return {
          ...viewSettings,
          filter: parentFilter,
        };
      }
    }

    case ViewChangeTypes.VIEW_FILTERS_REMOVE: {
      if (!viewSettings.filter || !data.path) {
        return {
          ...viewSettings,
          filter: undefined,
        };
      } else {
        const parentFilter = cloneDeep(viewSettings.filter);

        // If a view has filters, the top level filter for the view will always be a boolean
        // usage type
        if (isFilterWithUsageType(parentFilter)) {
          const nestedFilterData = getNestedFilter(
            parentFilter.value,
            data.filterId,
            data.path
          );

          // A filter will be defined if there is a valid filter with an id matching the acted
          // upon filter. This validates that a collaborator has not removed the filter whose
          // value is being set.
          if (nestedFilterData) {
            const [filter, index] = nestedFilterData as [DatabaseFilter, number];
            // Operations are done on the direct parent of the altered or new filter
            const directParent = getDirectParentFilter(parentFilter, filter, index) as
              | DatabaseFilter
              | undefined;

            if (directParent && isFilterWithUsageType(directParent)) {
              directParent.value = [
                ...directParent.value.slice(0, index),
                ...directParent.value.slice(index + 1),
              ];

              // Removes the filter group usage boolean filter if there are no children
              // filters remaining
              if (directParent.value.length === 0) {
                const filterGroupData = getNestedFilter(
                  parentFilter.value,
                  directParent.id,
                  data.path.slice(0, -1)
                );

                if (filterGroupData) {
                  const filterGroupIndex = filterGroupData[1];

                  parentFilter.value = [
                    ...parentFilter.value.slice(0, filterGroupIndex),
                    ...parentFilter.value.slice(filterGroupIndex + 1),
                  ];
                }
              }
            }

            // Removes all filters if there are no children filters remaining
            if (parentFilter.value.length === 0) {
              return {
                ...viewSettings,
                filter: undefined,
              };
            }
          }
        }

        return {
          ...viewSettings,
          filter: parentFilter,
        };
      }
    }

    case ViewChangeTypes.VIEW_FILTERS_CLEAR: {
      return {
        ...viewSettings,
        filter: undefined,
      };
    }

    case ViewChangeTypes.VIEW_SEARCH: {
      return {
        ...viewSettings,
        search: data.value,
      };
    }

    case ViewChangeTypes.VIEW_CALCULATIONS_SET: {
      return {
        ...viewSettings,
        calculations: {
          ...viewSettings.calculations,
          [data.fieldKey]: data.value,
        },
      };
    }

    case ViewChangeTypes.VIEW_CALCULATIONS_REMOVE: {
      return {
        ...viewSettings,
        calculations: omit(viewSettings.calculations, [data.fieldKey]),
      };
    }

    default: {
      return viewSettings;
    }
  }
}

export const getViewChange = (
  data: ViewChangeType,
  view: DatabaseView
): DatabaseChange | undefined => {
  switch (data.type) {
    case ViewChangeTypes.LAYOUT_TYPE:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_TYPE,
        viewId: view.id,
        newLayoutType: data.value,
      };
    case ViewChangeTypes.LAYOUT_CARD_WIDTH:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_CARD_WIDTH,
        viewId: view.id,
        newCardWidth: data.value,
      };
    case ViewChangeTypes.LAYOUT_COVER_FIELD_ID:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_COVER_FIELD_ID,
        viewId: view.id,
        newCoverFieldId: data.value,
      };
    case ViewChangeTypes.LAYOUT_SHOW_FIELD_NAMES:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_FIELD_NAMES,
        viewId: view.id,
        newShowFieldNames: data.value,
      };
    case ViewChangeTypes.LAYOUT_GROUP_FIELD_ID:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_GROUP_FIELD_ID,
        viewId: view.id,
        newGroupFieldId: data.value,
      };
    case ViewChangeTypes.VIEW_GROUPING_FIELD:
      return {
        type: DatabaseChangeType.SET_GROUPING_FIELD,
        viewId: view.id,
        fieldId: data.fieldId,
        isSubgroup: data.isSubgroup,
        manualGroupOrder: data.manualGroupOrder,
      };
    case ViewChangeTypes.VIEW_GROUPING_SORT_TYPE:
      return {
        type: DatabaseChangeType.SET_GROUPING_SORT_TYPE,
        viewId: view.id,
        sortType: data.sortType,
        isSubgroup: data.isSubgroup,
      };
    case ViewChangeTypes.VIEW_GROUPING_MANUAL_GROUP_ORDER:
      return {
        type: DatabaseChangeType.SET_GROUPING_MANUAL_GROUP_ORDER,
        viewId: view.id,
        manualGroupOrder: data.manualGroupOrder,
        isSubgroup: data.isSubgroup,
      };
    case ViewChangeTypes.VIEW_GROUPING_CLEAR:
      return {
        type: DatabaseChangeType.CLEAR_GROUPING,
        viewId: view.id,
        isSubgroup: data.isSubgroup,
      };
    case ViewChangeTypes.LAYOUT_DATE_FIELD_ID:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_DATE_FIELD_ID,
        viewId: view.id,
        newDateFieldId: data.value,
      };
    case ViewChangeTypes.LAYOUT_SHOW_NUMBERED_ENTRIES:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_LAYOUT_SHOW_NUMBERED_ENTRIES,
        viewId: view.id,
        newShowNumberedEntries: data.value,
      };
    case ViewChangeTypes.VIEW_NAME:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_NAME,
        viewId: view.id,
        newName: data.value,
        delta: data.delta,
      };
    case ViewChangeTypes.VIEW_FIELDS:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_FIELDS,
        viewId: view.id,
        fieldKey: data.fieldKey,
        fieldValue: data.fieldValue,
        existingFields: data.fields,
      };
    case ViewChangeTypes.VIEW_SORT:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT,
        viewId: view.id,
        sort: data.sort,
      };
    case ViewChangeTypes.VIEW_SORT_UPDATE_VALUE:
      return {
        type: DatabaseChangeType.UPDATE_SAVED_VIEW_SORT_VALUE,
        viewId: view.id,
        fieldId: data.fieldId,
        ascending: data.ascending,
        customOrder: data.customOrder,
      };
    case ViewChangeTypes.VIEW_SORT_INSERT:
      return {
        type: DatabaseChangeType.INSERT_SAVED_VIEW_SORT,
        viewId: view.id,
        sort: data.sort,
        index: data.index,
      };
    case ViewChangeTypes.VIEW_SORT_APPEND:
      return {
        type: DatabaseChangeType.APPEND_SAVED_VIEW_SORT,
        viewId: view.id,
        sort: data.sort,
      };
    case ViewChangeTypes.VIEW_SORT_DELETE:
      return {
        type: DatabaseChangeType.DELETE_SAVED_VIEW_SORT,
        viewId: view.id,
        fieldId: data.fieldId,
      };
    case ViewChangeTypes.VIEW_SORT_REORDER:
      return {
        type: DatabaseChangeType.REORDER_SAVED_VIEW_SORT,
        viewId: view.id,
        fieldId: data.fieldId,
        newIndex: data.newIndex,
      };
    case ViewChangeTypes.VIEW_SORT_CLEAR:
      return {
        type: DatabaseChangeType.CLEAR_SAVED_VIEW_SORT,
        viewId: view.id,
      };
    case ViewChangeTypes.VIEW_FILTERS_SET:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_USAGE_TYPE:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_USAGE_TYPE,
        viewId: view.id,
        usageType: data.usageType,
        usageFilterId: data.usageFilterId,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_FIELD_ID:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_FIELD_ID,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_FILTER_TYPE:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_TYPE,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewFilterChangeType.VIEW_FILTERS_SET_VALUE:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_FILTER_VALUE,
        viewId: view.id,
        newFilter: data.filter,
        path: data.path,
        change: data.change,
      };
    case ViewChangeTypes.VIEW_FILTERS_APPEND:
      return {
        type: DatabaseChangeType.APPEND_SAVED_VIEW_FILTER,
        viewId: view.id,
        filterGroupId: data.filterGroupId,
        filterGroupPath: data.filterGroupPath,
        newFilter: data.filter,
        path: data.path,
      };
    case ViewChangeTypes.VIEW_FILTERS_REMOVE:
      return {
        type: DatabaseChangeType.REMOVE_SAVED_VIEW_FILTER,
        viewId: view.id,
        filterId: data.filterId,
        path: data.path,
      };
    case ViewChangeTypes.VIEW_FILTERS_CLEAR:
      return {
        type: DatabaseChangeType.CLEAR_SAVED_VIEW_FILTER,
        viewId: view.id,
      };
    case ViewChangeTypes.VIEW_CALCULATIONS_SET:
      return {
        type: DatabaseChangeType.SET_SAVED_VIEW_CALCULATIONS,
        viewId: view.id,
        fieldId: data.fieldKey,
        value: data.value,
      };
    case ViewChangeTypes.VIEW_CALCULATIONS_REMOVE:
      return {
        type: DatabaseChangeType.REMOVE_SAVED_VIEW_CALCULATION,
        viewId: view.id,
        fieldId: data.fieldKey,
      };
  }
};

/**
 * Returns the count of hidden fields before the given index
 * */
export const getHiddenFieldsCountBeforeIndex = (
  hiddenFields: string[],
  allFields: readonly string[],
  idx: number
) =>
  hiddenFields.filter((hiddenField) => {
    const hiddenFieldIndex = allFields?.indexOf(hiddenField);
    return hiddenFieldIndex >= 0 && hiddenFieldIndex <= idx;
  }).length;

/**
 * Returns the hidden fields of the given view settings
 * */
export const getHiddenFields = (fields: DatabaseViewSettings['fields'] | undefined) =>
  Object.entries(fields || {})
    .filter(([fieldId, value]) => !value && fieldId !== DEFAULT_FIELD_VISIBILITY_ID)
    .map(([key, _]) => key);

/**
 * Returns the non-hidden fields of the given view settings
 * */
export const getVisibleFields = (
  database: Database,
  viewSettings: DatabaseViewSettings | undefined
) => {
  if (viewSettings) {
    return database.fields.filter((fieldId) => {
      return shouldShowField(viewSettings.fields, fieldId);
    });
  } else {
    return [];
  }
};

export const areAllFieldsHidden = (viewSettings: DatabaseViewSettings | undefined) =>
  viewSettings?.fields
    ? Object.entries(viewSettings.fields)
        .filter(([fieldId, _]) => fieldId !== DEFAULT_FIELD_VISIBILITY_ID)
        .every(([_, value]) => Boolean(!value))
    : false;

/**
 * If the database has hidden fields, the given index is incremented by the count of the hidden fields which are before the index
 * */
export const getNewIndexWithHiddenFields = (
  idx: number,
  allFields: readonly string[],
  hiddenFields: string[] | undefined
) => {
  if (hiddenFields?.length) {
    const hiddenFieldsCountBeforeIndex = getHiddenFieldsCountBeforeIndex(
      hiddenFields,
      allFields,
      idx
    );

    return hiddenFieldsCountBeforeIndex > 0 ? idx + hiddenFieldsCountBeforeIndex : idx;
  } else {
    return idx;
  }
};

export const isFilterSetPopulated = (filter: DatabaseFilter | undefined): boolean => {
  if (!filter) {
    return false;
  }

  switch (filter.type) {
    case DatabaseFilterType.AND:
    case DatabaseFilterType.OR:
      return filter.value.some((f) => isFilterSetPopulated(f));
    case DatabaseFilterType.EQUALS:
    case DatabaseFilterType.CONTAINS:
    case DatabaseFilterType.EMPTY:
    case DatabaseFilterType.GREATER:
    case DatabaseFilterType.LESS:
      return filter.fieldId !== '';
    default:
      return false;
  }
};

export const getIsFilterOrSortingActive = (
  viewSettings: DatabaseViewSettings
): boolean => {
  return getIsSortingActive(viewSettings) || isFilterSetPopulated(viewSettings.filter);
};

export const getIsSortingActive = (viewSettings: DatabaseViewSettings): boolean => {
  return viewSettings.sorts.length > 0;
};
